// TODO: ensure invariant on this entire page that the user has to be logged in to access this page
import {
  Link,
  createFileRoute,
  stripSearchParams,
  useNavigate,
} from "@tanstack/react-router";
import {
  BookOpenIcon,
  Check,
  ChevronsUpDown,
  CopyIcon,
  Save,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { z } from "zod/v4";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { Suspense, useCallback, useId, useState } from "react";
import { toast } from "sonner";
import { HTTPError } from "ky";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { userInfoQueryOptions } from "@/lib/queries/userInfo";
import { api } from "@/lib/http";
import { passwordRegex, passwordRegexErrorMessage } from "@/lib/auth";
import { PasswordInput } from "@/components/ui/passwordInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, formatNumber } from "@/lib/utils";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LoadingSpinner } from "@/components/loading";
import { ApiKeysList } from "@/components/api-keys/ApiKeysList";
import { apiKeysQueryOptions } from "@/lib/queries/apiKeys";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  useUserDeleteReasons,
  userDeleteReasonsQueryOptions,
} from "@/lib/queries/userDeleteReasons";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { userPlanQueryOptions } from "@/lib/queries/payment/userPlan";
import { userBalanceQueryOptions } from "@/lib/queries/userBalance";
import { CoinIcon } from "@/components/icons/coin";
import { SubmitterText } from "@/components/ui/submit-button";
import { tokenPacketsQueryOptions } from "@/lib/queries/payment/tokenPackets";
import { lifetimeDate } from "@/lib/utils/date";
import { generateDefaultValue, searchBoolean } from "@/lib/utils/zod";
import { useLogout } from "@/lib/hooks/auth";
import {
  apiSubscriptionQueryOptions,
  useApiSubscription,
} from "@/lib/queries/payment/apiSubscription";
import { useCancelSubscription } from "@/lib/mutations/plan";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const accountPageSearchParamsSchema = z.object({
  tab: z.enum(["profile", "subscription", "api-agent"]).catch("profile"),
  buyMoreTokens: searchBoolean,
});

const accountPageDescriptions = {
  profile:
    "Manage your profile with ease - change your password, update your photo, or delete your account from one secure place.",
  subscription:
    "Manage your subscription settings - view or change your plan, cancel anytime, buy more tokens, or earn rewards through referrals.",
  "api-agent":
    "Call Polygraf AI’s RESTful endpoints to detect synthetic content, redact PII, analyze voice deepfakes, and humanize text—complete with code samples and deployable in local or on-premises environments.",
} as const satisfies Record<
  z.infer<typeof accountPageSearchParamsSchema>["tab"],
  string
>;

export const Route = createFileRoute("/_app/account")({
  validateSearch: accountPageSearchParamsSchema,
  search: {
    middlewares: [stripSearchParams({ buyMoreTokens: false })],
  },
  component: RouteComponent,
  async loader({ context: { queryClient } }) {
    [
      userDeleteReasonsQueryOptions,
      userBalanceQueryOptions,
      userPlanQueryOptions,
      tokenPacketsQueryOptions,
      apiSubscriptionQueryOptions,
      apiKeysQueryOptions,
    ].forEach((opts) => queryClient.ensureQueryData(opts as unknown as any));
    await queryClient.ensureQueryData(userInfoQueryOptions);
  },
  staticData: {
    title: "Account Settings & Info",
  },
  head: ({
    match: {
      search: { tab },
    },
  }) => ({
    meta: [
      {
        name: "description",
        content: accountPageDescriptions[tab],
      },
    ],
  }),
});

function RouteComponent() {
  const { tab = "profile" } = Route.useSearch();
  const navigate = useNavigate({
    from: Route.fullPath,
  });
  const apiSubscription = useApiSubscription();
  return (
    <div className="px-4 py-10">
      <h1 className="mb-6 text-xl font-semibold">Account Settings</h1>

      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            search: { tab: value as "profile" | "subscription" | "api-agent" },
            replace: true,
          })
        }
        className="space-y-6"
      >
        <TabsList className="w-full dark:bg-transparent">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          {apiSubscription.data?.status === "ACTIVE" && (
            <TabsTrigger value="api-agent">API Agent</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <div className="rounded-lg border bg-card p-6">
            <AccountForm />
          </div>
          <div className="rounded-lg border bg-card p-6">
            <h2 className="mb-6 text-lg font-medium">Change Password</h2>
            <PasswordForm />
          </div>
          <div className="rounded-lg border bg-card p-6">
            <ProfileForm />
          </div>
        </TabsContent>

        <TabsContent value="subscription">
          <Suspense fallback={<LoadingSpinner />}>
            <SubscriptionsTab />
          </Suspense>
        </TabsContent>

        {apiSubscription.data?.status === "ACTIVE" && (
          <TabsContent value="api-agent">
            <Suspense fallback={<LoadingSpinner />}>
              <ApiAgentTab />
            </Suspense>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

const defaultCategories: ReadonlyArray<string> = [
  "Student",
  "Professional",
  "Academia",
];
const AccountFormSchema = z.object({
  full_name: z.string().min(1, "Full name is required"),
  email: z.email("Invalid email address").min(1, "Email is required"),
  category: z.string().min(1, "Category is required"),
});
type AccountFormSchema = z.infer<typeof AccountFormSchema>;

function AccountForm() {
  const userInfo = useSuspenseQuery(userInfoQueryOptions);

  const { queryClient } = Route.useRouteContext();

  const form = useForm({
    resolver: standardSchemaResolver(AccountFormSchema),
    defaultValues: {
      ...userInfo.data.info,
      category: userInfo.data.info?.category ?? "",
    },
  });

  const changeAccountDetails = useMutation({
    async mutationFn(json: AccountFormSchema) {
      const res = await api.auth.patch<{ detail: string }>(
        "users/profile-update",
        {
          json,
        },
      );
      return res.json();
    },
    onSuccess() {
      queryClient.refetchQueries({ queryKey: userInfoQueryOptions.queryKey });
    },
  });

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit((formData) => {
          changeAccountDetails.mutate(formData);
        })}
      >
        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem className="flex items-center">
              <FormLabel className="w-full">
                Full name <span className="text-destructive">*</span>
              </FormLabel>
              <div className="flex w-full flex-col gap-2">
                <FormControl>
                  <Input
                    autoComplete="name"
                    placeholder="Full name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          disabled
          render={({ field }) => (
            <FormItem className="flex items-center">
              <FormLabel className="w-full">
                Email<span className="text-destructive">*</span>
              </FormLabel>
              <div className="flex w-full flex-col gap-2">
                <FormControl>
                  <Input
                    autoComplete="email"
                    placeholder="Email"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>What category best describes you?</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "w-[200px] justify-between",
                        !field.value && "text-muted-foreground",
                      )}
                    >
                      {field.value ? field.value : "Select a category"}
                      <ChevronsUpDown className="opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0">
                  <Command>
                    <CommandInput
                      onValueChange={(value) => {
                        form.setValue("category", value);
                      }}
                      className="h-9"
                      placeholder="Search category..."
                    />
                    <CommandGroup>
                      {defaultCategories.includes(field.value) ||
                      field.value === "" ? null : (
                        <CommandItem
                          value={field.value}
                          key={field.value}
                          onSelect={() =>
                            form.setValue("category", field.value)
                          }
                        >
                          {field.value} <Check className="ml-auto" />
                        </CommandItem>
                      )}
                      {defaultCategories.map((category) => (
                        <CommandItem
                          value={category}
                          key={category}
                          onSelect={() => form.setValue("category", category)}
                        >
                          {category}
                          <Check
                            className={cn(
                              "ml-auto",
                              category === field.value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" variant="outline" size="sm">
            {changeAccountDetails.isPending ? (
              <>
                <LoadingSpinner /> Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 size-4" />
                Save
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

const PasswordFormSchema = z
  .object({
    current_password: z
      .string("Please enter your current password")
      .min(1, "Please enter your current password"),
    new_password: z
      .string("Please enter new password")
      .regex(passwordRegex, passwordRegexErrorMessage),
    new_password_confirm: z
      .string("Please confirm new password")
      .regex(passwordRegex, passwordRegexErrorMessage),
  })
  .refine((data) => data.new_password === data.new_password_confirm, {
    error: "Passwords do not match",
    path: ["new_password_confirm"],
  });
type PasswordFormSchema = z.infer<typeof PasswordFormSchema>;
function PasswordForm() {
  const form = useForm<PasswordFormSchema>({
    resolver: standardSchemaResolver(PasswordFormSchema),
  });

  const changePasswordMutation = useMutation({
    async mutationFn(json: PasswordFormSchema) {
      await api.auth.post("users/change-password", { json });
    },
    onSuccess() {
      form.reset();
    },
  });

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit((formData) => {
          changePasswordMutation.mutate(formData);
        })}
      >
        {/* For autocomplete/password managers */}
        <Input autoComplete="email" className="hidden" />

        <FormField
          control={form.control}
          name="current_password"
          render={({ field }) => (
            <FormItem className="flex items-center">
              <FormLabel className="w-full">
                Current Password <span className="text-destructive">*</span>
              </FormLabel>
              <div className="flex w-full flex-col gap-2">
                <FormControl>
                  <PasswordInput autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="new_password"
          render={({ field }) => (
            <FormItem className="flex items-center">
              <FormLabel className="w-full">
                New Password <span className="text-destructive">*</span>
              </FormLabel>
              <div className="flex w-full flex-col gap-2">
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="new_password_confirm"
          render={({ field }) => (
            <FormItem className="flex items-center">
              <FormLabel className="w-full">
                Confirm New Password <span className="text-destructive">*</span>
              </FormLabel>
              <div className="flex w-full flex-col gap-2">
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" variant="outline" size="sm">
            {changePasswordMutation.isPending ? (
              <>
                <LoadingSpinner /> Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 size-4" />
                Save
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function ProfileForm() {
  const userInfo = useSuspenseQuery(userInfoQueryOptions);
  const { queryClient } = Route.useRouteContext();

  // possible improvement: show the image in the avatar
  // when the user selects a new image
  const [image, setImage] = useState<File | null>(null);
  const changePfp = useMutation({
    async mutationFn(newImage: File) {
      const formData = new FormData();
      formData.append("profile_picture", newImage);
      await api.auth.post("users/v2/profile-picture", {
        body: formData,
      });
    },
    async onSuccess() {
      await queryClient.refetchQueries({
        queryKey: userInfoQueryOptions.queryKey,
      });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium">
          Profile photo <span className="text-destructive">*</span>
        </label>
        <p className="mt-1 text-sm text-muted-foreground">
          This will be displayed on your profile.
        </p>
      </div>

      <div className="flex items-start gap-6">
        {/* in here we should have an invariant that the user is definitely logged in */}
        <Avatar className="size-20 border bg-muted">
          <AvatarImage src={userInfo.data.info?.profile_pic} />
          <AvatarFallback>{userInfo.data.info?.full_name}</AvatarFallback>
        </Avatar>

        <div className="flex-1">
          <div className="flex flex-col gap-2">
            <Input
              onChange={(e) => {
                if (e.target.files?.length) setImage(e.target.files[0]);
              }}
              type="file"
              accept="image/*"
            />
            <span className="text-sm text-muted-foreground">
              or drag and drop
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          disabled={changePfp.isPending || !image}
          aria-disabled={changePfp.isPending || !image}
          onClick={() => {
            if (changePfp.isPending) return;
            else if (image) changePfp.mutate(image);
          }}
          variant="outline"
          size="sm"
        >
          {changePfp.isPending ? (
            <>
              <LoadingSpinner />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 size-4" />
              Save
            </>
          )}
        </Button>
      </div>

      <div className="mt-6 border-t pt-6">
        <div className="space-y-2">
          <h3 className="text-lg font-medium">Delete your account</h3>
          <p className="text-sm text-muted-foreground">
            Deleting your account will permanently erase all your data
            associated with our services, and this action cannot be undone.
          </p>
          <div className="flex justify-end">
            <DeleteAccountButton />
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteAccountButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AlertDialog>
        <AlertDialogTrigger
          className={cn(
            buttonVariants({ variant: "destructive", size: "sm" }),
            "dark:bg-gray-700 dark:hover:bg-gray-600",
          )}
        >
          Delete your account
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              account and remove your data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setOpen(true)}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>We're sorry to see you go...</DialogTitle>
            <DialogDescription>
              Please tell us why you're leaving.
            </DialogDescription>
          </DialogHeader>

          <DeleteAccountForm />
        </DialogContent>
      </Dialog>
    </>
  );
}

const UserDeleteFormSchema = z.object({
  reasons: z.array(z.uuid()),
  message: z.string().optional(),
});
type UserDeleteFormSchema = z.infer<typeof UserDeleteFormSchema>;

function DeleteAccountForm() {
  const deleteReasons = useUserDeleteReasons();

  const form = useForm({
    resolver: standardSchemaResolver(UserDeleteFormSchema),
    defaultValues: generateDefaultValue(UserDeleteFormSchema),
  });

  const [other, setOther] = useState(false);

  const logout = useLogout();
  const deleteUser = useMutation({
    async mutationFn(formData: UserDeleteFormSchema) {
      await api.auth.delete("users/delete-user", {
        json: { ...formData, source: "account_delete" },
      });
    },
    onSuccess() {
      toast.success("Your account has been successfully deleted!");
      logout();
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((formData) => deleteUser.mutate(formData))}
      >
        <FormField
          control={form.control}
          name="reasons"
          render={() => (
            <FormItem>
              <FormLabel> Check all that apply:</FormLabel>
              {deleteReasons.isSuccess ? (
                <>
                  {" "}
                  {deleteReasons.data.map(({ id, reason }) => (
                    <FormField
                      key={id}
                      control={form.control}
                      name="reasons"
                      render={({ field }) => (
                        <FormItem
                          key={id}
                          className="flex flex-row items-start space-y-0 space-x-3"
                        >
                          <FormControl>
                            <Checkbox
                              checked={(
                                field.value as Array<string> | undefined
                              )?.includes(id)}
                              onCheckedChange={
                                (checked) =>
                                  checked
                                    ? field.onChange([
                                        ...((field.value as
                                          | Array<string>
                                          | undefined) ?? []),
                                        id,
                                      ])
                                    : field.onChange(
                                        (
                                          field.value as
                                            | Array<string>
                                            | undefined
                                        )?.filter((value) => value !== id),
                                      ) // value could be uninitialized
                              }
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal">
                            {reason}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                  <FormField // fake formfield to keep same design
                    control={form.control}
                    name="message"
                    render={() => (
                      <FormItem
                        key="other"
                        className="flex flex-row items-start space-y-0 space-x-3"
                      >
                        <FormControl>
                          <Checkbox
                            checked={other}
                            onCheckedChange={(checked) => {
                              setOther(Boolean(checked));
                              if (!checked) form.setValue("message", undefined);
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">
                          Something Else
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  {!other ? null : (
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem
                          key="other"
                          className="flex flex-row items-start space-y-0 space-x-3"
                        >
                          <FormControl>
                            <Textarea
                              placeholder="Tell us how can we improve our service"
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                </>
              ) : deleteReasons.isLoading ? (
                <span>
                  <LoadingSpinner />
                  Loading...
                </span>
              ) : (
                "Error..."
              )}
            </FormItem>
          )}
        />
        <Button type="submit" className="mt-2">
          Delete Account
        </Button>
      </form>
    </Form>
  );
}

function SubscriptionsTab() {
  const userPlan = useSuspenseQuery(userPlanQueryOptions);
  const planInfo = userPlan.data.info;
  const isEnterpise = planInfo?.plan.is_enterprise;
  const isFree = Number(planInfo?.plan.price) == 0;
  const isForeignPlan = planInfo && planInfo.source !== "Polygraf";

  const cancelledPlan = planInfo && planInfo.cancellation_date !== null;

  return (
    <>
      <span>
        For questions about plans,{" "}
        <a
          href="https://polygraf.ai/contact/"
          target="_blank"
          className="underline"
        >
          Contact Us
        </a>
      </span>
      <div className="my-4">
        <CurrentPlan />
      </div>
      {isEnterpise || (
        <>
          {!(isFree || isForeignPlan || cancelledPlan) && (
            <div className="my-4">
              <SubscriptionManagement />
            </div>
          )}
          {!isForeignPlan && (
            <div className="my-4">
              <EnterprisePlanActivate />
            </div>
          )}
        </>
      )}
      <div className="my-4">
        <ReferralSection />
      </div>
    </>
  );
}

function CurrentPlan() {
  const plan = useSuspenseQuery(userPlanQueryOptions);
  const balance = useSuspenseQuery(userBalanceQueryOptions);

  const planInfo = plan.data.info;
  const foreignPlan = planInfo && planInfo.source !== "Polygraf";
  const planCancellationDate = planInfo?.cancellation_date
    ? new Date(planInfo.cancellation_date).toLocaleDateString()
    : null;

  return (
    <div className="rounded-lg border bg-input/30 p-5 dark:border-gray-700 dark:bg-transparent">
      <h2 className="mb-4 text-2xl font-medium text-gray-800 dark:text-white">
        {planInfo?.plan.name}
      </h2>

      {foreignPlan && (
        <h6 className="mx-auto mt-2 text-destructive-foreground">
          Your subscription is handled by {planInfo.source} and cannot be
          managed through this dashboard
        </h6>
      )}
      {planCancellationDate && (
        <h6 className="mx-auto mt-2 text-destructive-foreground">
          Your subscription has been cancelled and will expire at{" "}
          {planCancellationDate}
        </h6>
      )}

      <div className="mb-4 rounded-lg border bg-white p-5 dark:bg-transparent">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-gray-700 dark:text-white">Balance</span>
          <span className="font-medium text-gray-500 dark:text-white">
            {formatNumber(balance.data.plan_balance)}
          </span>
        </div>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-gray-700 dark:text-white">
            Purchased Tokens
          </span>
          <span className="font-medium text-gray-500 dark:text-white">
            {formatNumber(balance.data.token_balance)}
          </span>
        </div>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-gray-700 dark:text-white">Referral Tokens</span>
          <span className="font-medium text-gray-500 dark:text-white">
            {formatNumber(balance.data.referral_token_balance)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-700 dark:text-white">Plan Renewal</span>
          <span className="font-medium text-gray-500 dark:text-white">
            {planInfo?.end_date
              ? new Date(planInfo.end_date).getTime() === lifetimeDate
                ? "Never"
                : new Date(planInfo.end_date).toLocaleDateString(undefined, {
                    dateStyle: "long",
                  })
              : "---"}
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <BuyMoreTokens />

        {!foreignPlan && (
          <Link to="/pricing" className={buttonVariants()}>
            Explore all plans
          </Link>
        )}
      </div>
    </div>
  );
}

function BuyMoreTokens() {
  const buyMoreTokens = Route.useSearch({
    select: (s) => s.buyMoreTokens,
  });

  const [open, setOpen] = useState(buyMoreTokens);
  const [selectedPacket, setSelectedPacket] = useState<string | null>(null);
  const tokenPackets = useQuery({
    ...tokenPacketsQueryOptions,
    select(data) {
      data.sort((a, b) => a.token_amount - b.token_amount);
      return data;
    },
  });

  const buySelectedPacket = useMutation({
    async mutationFn(id: string) {
      return await api.payment
        .post<{
          checkout_url: string;
        }>(`v1/payments/token-checkout-session/${id}`)
        .json();
    },
    onSuccess(data) {
      window.location.href = data.checkout_url;
    },
  });

  return (
    <div className="flex justify-end gap-3">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="border-gray-500 text-gray-500 hover:bg-gray-50"
          >
            Buy more tokens
          </Button>
        </DialogTrigger>
        <DialogContent className="gap-0 p-0 sm:max-w-md">
          {tokenPackets.isLoading ? (
            <LoadingSpinner />
          ) : (
            tokenPackets.isSuccess && (
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <DialogTitle className="text-3xl font-bold text-gray-900 dark:text-white">
                    Buy more tokens
                  </DialogTitle>
                </div>

                <p className="mt-3 mb-6 text-gray-500 dark:text-white">
                  Purchased tokens are added to your plan and never expire —
                  they'll remain in your account until you use them.
                </p>

                <div className="space-y-4">
                  <p className="text-xl font-medium">Select token amount:</p>

                  {tokenPackets.data.map((pkg) => (
                    <div
                      key={pkg.id}
                      className={`relative flex cursor-pointer items-center justify-between rounded-lg border p-4 ${
                        selectedPacket === pkg.id
                          ? "border-gray-500"
                          : "border-gray-200"
                      }`}
                      onClick={() => setSelectedPacket(pkg.id)}
                    >
                      <div className="flex items-center gap-2">
                        <CoinIcon />
                        <span className="text-xl font-medium">
                          {pkg.token_amount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex flex-col items-end">
                        {/* {pkg.bestValue && (
                          <div className="absolute -top-3 right-4 rounded-full bg-gray-500 px-3 py-1 text-xs font-bold text-white">
                            BEST VALUE
                          </div>
                        )} */}
                        <span className="text-xl font-medium">
                          ${pkg.price}
                        </span>
                      </div>
                    </div>
                  ))}

                  <Button
                    className="mt-4 w-full bg-gray-700 py-6 text-lg text-white hover:bg-gray-600"
                    disabled={selectedPacket === null}
                    onClick={() => buySelectedPacket.mutate(selectedPacket!)}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubscriptionManagement() {
  return (
    <div className="rounded-lg border border-gray-200 bg-input/30 p-6 dark:border-gray-700 dark:bg-transparent">
      <h2 className="text-xl font-medium text-gray-900 dark:text-white">
        Subscription Management
      </h2>
      <p className="mt-2 text-gray-600 dark:text-white">
        You can cancel your current subscription at any time. Your access will
        remain active until the end of your billing period.
      </p>
      <CancelSubscriptionButton />
    </div>
  );
}

function CancelSubscriptionButton() {
  const [open, setOpen] = useState(false);

  const queryClient = useQueryClient();
  const cancelPlan = useMutation({
    async mutationFn() {
      return await api.payment
        .post<{ detail: string }>("v1/plans/cancel-plan")
        .json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userPlanQueryOptions);
      setOpen(false);
    },
  });
  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>
          <Button
            variant="outline"
            className="mt-4 border-gray-500 text-gray-500 hover:bg-gray-50 hover:text-gray-600"
          >
            Cancel Subscription
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Your plan will be canceled at the end of the current month. Until
              then, you have access to all features of your plan
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={() => cancelPlan.mutate()}>
              <SubmitterText
                submitting={cancelPlan.isPending}
                submittingChildren="Deleting..."
              >
                Continue
              </SubmitterText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={cancelPlan.isSuccess}
        onOpenChange={() => cancelPlan.reset()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your plan has been cancelled successfully</DialogTitle>
            <DialogDescription>{cancelPlan.data?.detail}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EnterprisePlanActivate() {
  const [accessCode, setAccessCode] = useState("");

  const queryClient = useQueryClient();
  const activateEnterprise = useMutation({
    async mutationFn(access_code: string) {
      try {
        await api.payment.post("v1/plans/enterprise-with-access-code", {
          json: { access_code },
        });
      } catch (e) {
        if (!(e instanceof HTTPError)) throw e;
        throw await e.response.json<{ detail: string }>();
      }
    },
    onError(_: { detail: string }) {},
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: userPlanQueryOptions.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: userBalanceQueryOptions.queryKey,
      });
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      activateEnterprise.mutate(accessCode);
    },
    [accessCode, activateEnterprise.mutate],
  );
  return (
    <Card className="border border-gray-200 bg-input/30 dark:border-gray-700 dark:bg-transparent">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Activate Your Enterprise Plan
          </h1>
          <p className="text-gray-600 dark:text-white">
            Enter your access code here to activate your Enterprise Plan.
          </p>
          <form onSubmit={handleSubmit} className="">
            <div className="mt-6 flex max-w-xl gap-10 space-y-4 md:flex-row">
              <Input
                type="text"
                placeholder="Enter access code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="h-12 dark:text-white"
              />
              <Button
                type="submit"
                disabled={accessCode.length == 0}
                variant="outline"
                className="h-12 border-gray-500 text-gray-500 hover:bg-gray-50 hover:text-gray-600 sm:w-full md:w-32"
              >
                <SubmitterText
                  submitting={activateEnterprise.isPending}
                  submittingChildren="Activating..."
                >
                  Activate
                </SubmitterText>
              </Button>
            </div>
            {activateEnterprise.isError && (
              <span className="text-destructive">
                {activateEnterprise.error.detail}
              </span>
            )}
            {activateEnterprise.isSuccess && (
              <span className="text-green-300">Success!</span>
            )}
          </form>
        </div>
      </CardContent>
    </Card>
  );
}
const emailSchema = z.email();
function ReferralSection() {
  const referralCode = useSuspenseQuery({
    ...userInfoQueryOptions,
    select(data) {
      const url = new URL(window.origin);
      url.pathname = "/auth/signup";
      url.searchParams.set("referral_code", data.info!.referral_code);
      return url.href;
    },
  });

  const emailInputId = useId();

  const [email, setEmail] = useState("");

  const inviteFriend = useMutation({
    async mutationFn(target_email: string) {
      return await api.auth.post("referral/invitations", {
        json: { target_email },
      });
    },
    onSuccess() {
      toast.success("Invitation sent successfully");
      setEmail("");
    },
    async onError(error: HTTPError<{ detail: string }>) {
      try {
        const { detail } = await error.response.json();

        if (detail) toast.error(detail);
        else toast.error("Something went wrong");
      } catch {
        toast.error("Something went wrong");
      }
    },
  });

  const [copied, setCopied] = useState(false);
  return (
    <Card className="rounded-xl border border-gray-200 bg-input/30 p-8 dark:border-gray-700 dark:bg-transparent">
      <div className="grid gap-8 md:grid-cols-2">
        {/* Left Section */}
        <div>
          <h2 className="mb-6 text-2xl font-semibold">
            Refer Friends to Earn Tokens
          </h2>

          <div className="relative mb-6">
            <Input
              value={referralCode.data}
              readOnly
              data-copied={copied ? "true" : undefined}
              className="pointer-events-none h-14 bg-white pr-12 text-gray-600 transition-[padding-right] data-copied:pr-30"
            />
            <Button
              variant="ghost"
              disabled={copied}
              onClick={() =>
                navigator.clipboard.writeText(referralCode.data).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1000);
                })
              }
              className="absolute top-1/2 right-3 -translate-y-1/2 transition-[width]"
              aria-label="Copy referral link"
            >
              {copied && "Copied!"}
              <CopyIcon className="h-5 w-5 text-gray-400" />
            </Button>
          </div>

          <div className="flex w-full gap-3">
            <Input
              id={emailInputId}
              placeholder="Your friend's email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14"
            />
            <Button
              variant="outline"
              disabled={!emailSchema.safeParse(email).success}
              onClick={() => inviteFriend.mutate(email)}
              className="h-14 border-gray-500 px-8 text-gray-500 hover:bg-gray-50 hover:text-gray-600 dark:border-gray-700"
            >
              Invite
            </Button>
          </div>
        </div>

        {/* Right Section */}
        <div>
          <h2 className="mb-6 text-2xl font-semibold text-gray-600">
            How to Earn Free Tokens?
          </h2>

          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="text-xl font-semibold text-gray-600">01</div>
              <div className="text-xl">Share your link with your friends</div>
            </div>

            <div className="flex gap-4">
              <div className="text-xl font-semibold text-gray-600">02</div>
              <div className="text-xl">
                Earn 2,500 tokens/month when friends sign up
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-xl font-semibold text-gray-600">03</div>
              <div className="text-xl">
                Use tokens to access the AI content detector
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

// Cancel Subscription Confirmation Modal Component
function CancelSubscriptionModal({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Are you absolutely sure?
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Your plan will be canceled at the end of the current month. Until
            then, you have access to all features of your plan.
          </p>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-gray-700 text-white hover:bg-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
          >
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Cancel Subscription Success Modal Component
function CancelSubscriptionSuccessModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <div className="relative">
          <div className="pr-8">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Your plan has been cancelled successfully
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                You will continue to have full access to all features until the end of your billing period, after which your plan will be cancelled.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ApiAgentTab = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<
    "last24hour" | "lastweek" | "lastmonth"
  >("last24hour");

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const cancelSubscription = useCancelSubscription();
  const navigate = useNavigate({
    from: Route.fullPath,
  });
  const { data: apiStats, isLoading } = useQuery({
    queryKey: ["api-usage-statistics", selectedPeriod],
    queryFn: async () => {
      const response = await api.apiSvc
        .get(`v1/api-usage/statistics?period=${selectedPeriod}`)
        .json();
      return response as {
        range_data: Array<{ hour?: number; date?: string; count: number }>;
        total_api_calls: number;
        total_scanned_pages: number;
      };
    },
  });

  const formatXAxisLabel = (value: number | string) => {
    if (selectedPeriod === "last24hour") {
      return `${value}:00`;
    }
    if (typeof value === "string") {
      // Format date for week/month views
      const date = new Date(value);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
    return `Day ${value}`;
  };

  const formatPeriodLabel = (period: string) => {
    switch (period) {
      case "last24hour":
        return "Last 24 Hours";
      case "lastweek":
        return "Last Week";
      case "lastmonth":
        return "Last Month";
      default:
        return period;
    }
  };

  const getChartData = () => {
    if (!apiStats?.range_data) return [];

    return apiStats.range_data.map((item, index) => {
      if (selectedPeriod === "last24hour") {
        return {
          xAxis: item.hour ?? index,
          count: item.count,
        };
      } else {
        return {
          xAxis: item.date ?? index,
          count: item.count,
        };
      }
    });
  };

  const chartData = getChartData();

  // Function to handle cancel subscription
  const handleCancelSubscription = () => {
    cancelSubscription.mutate(undefined, {
      onSuccess: () => {
        setShowCancelModal(false);
        setShowSuccessModal(true);
        // change tab to profile
        navigate({ search: { tab: "profile" }, replace: true });
      },
      onError: (error) => {
        console.error("Failed to cancel subscription:", error);
        // You might want to show a toast notification here
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Title and Description */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">
            API Usage Analytics
          </h2>
          <p className="text-muted-foreground">
            Manage your API usage and billing.
          </p>
        </div>
        <Button asChild variant="default" className="bg-gray-700 text-white">
          <Link to="/api-agent-doc" target="_blank">
            <BookOpenIcon className="h-4 w-4" />
            API Documentation
          </Link>
        </Button>
      </div>

      {/* Period Selection */}
      <div className="flex items-center gap-4">
        <label htmlFor="period-select" className="text-sm font-medium">
          Time Period:
        </label>
        <Select
          value={selectedPeriod}
          onValueChange={(value: "last24hour" | "lastweek" | "lastmonth") =>
            setSelectedPeriod(value)
          }
        >
          <SelectTrigger id="period-select" className="w-[180px]">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="last24hour">Last 24 Hours</SelectItem>
            <SelectItem value="lastweek">Last Week</SelectItem>
            <SelectItem value="lastmonth">Last Month</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="rounded-lg bg-blue-100 p-2">
                <svg
                  className="h-5 w-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total API Calls
                </p>
                <p className="text-2xl font-bold">
                  {apiStats?.total_api_calls ?? 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="rounded-lg bg-green-100 p-2">
                <svg
                  className="h-5 w-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Pages Scanned
                </p>
                <p className="text-2xl font-bold">
                  {apiStats?.total_scanned_pages ?? 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">API Calls Over Time</h3>
              <p className="text-sm text-muted-foreground">
                {formatPeriodLabel(selectedPeriod)} - Usage pattern
                visualization
              </p>
            </div>

            {isLoading ? (
              <div className="flex h-64 items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : chartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="xAxis"
                      tickFormatter={formatXAxisLabel}
                      stroke="#888888"
                      fontSize={12}
                    />
                    <YAxis
                      stroke="#888888"
                      fontSize={12}
                      tickFormatter={(value) => value.toString()}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (
                          active &&
                          payload.length > 0 &&
                          label !== undefined
                        ) {
                          return (
                            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                              <p className="font-medium">
                                {selectedPeriod === "last24hour"
                                  ? `Time: ${formatXAxisLabel(label)}`
                                  : `Date: ${formatXAxisLabel(label)}`}
                              </p>
                              <p className="text-blue-600">
                                API Calls: {payload[0]?.value}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: "#3b82f6", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <svg
                    className="mx-auto mb-2 h-12 w-12 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <p>No data available for the selected period</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <ApiKeysList />

      {/* Subscription Management */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Subscription Management</h3>
              <p className="text-sm leading-relaxed text-gray-500">
                You can cancel your current subscription at any time. Your
                access will remain active until the end of your billing period.
              </p>
            </div>

            {/* Cancel Subscription Button */}
            <div className="flex justify-start">
              <Button
                className="flex items-center gap-2 px-6"
                onClick={() => setShowCancelModal(true)}
                disabled={cancelSubscription.isPending}
              >
                {cancelSubscription.isPending
                  ? "Canceling..."
                  : "Cancel Subscription"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cancel Subscription Confirmation Modal */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
      />

      {/* Cancel Subscription Success Modal */}
      <CancelSubscriptionSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </div>
  );
};
